/**
 * Verbose relative time — "5 weeks ago", "3 months ago". It is the Created
 * column of the guilds list, the Joined column of the Members table and the
 * Blocked column of the Blocked table, which is why it lives here rather than
 * in any of them: three columns one page apart that have to read the same.
 *
 * Each unit hands over on the DAY count, not on its own. Months are a 30-day
 * approximation, so a year is 12.17 of them: handing over when `month` reached
 * 12 left days 360 to 364 with no rule that claimed them, and they came out as
 * "0 years ago". The boundary between two units belongs to one number, and the
 * only number every unit here is derived from is the day.
 *
 * Those same five days are also why the month count is capped at eleven: 360
 * days is twelve 30-day months, and "12 months ago" printed next to a
 * threshold that calls 365 days a year reads like a bug in the other
 * direction.
 */
export function formatRelativeLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  if (day < 35) return plural(Math.floor(day / 7), "week");
  if (day < 365) return plural(Math.min(11, Math.floor(day / 30)), "month");
  return plural(Math.floor(day / 365), "year");
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}
